import React, { useState, useEffect, ReactEventHandler, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import useRefDimensions from '../../hooks/useRefDimensions'
import type { BackgroundOptions } from '../../models/types'
import BackgroundVideo from '../Utils/BackgroundVideo'
import BackgroundWebcam from '../Utils/BackgroundWebcam'

type BackgroundProps = {
  config: BackgroundOptions
  videoLoop?: boolean
  videoMuted?: boolean
  onVideoEnded?: ReactEventHandler<HTMLVideoElement>
  onVideoCanPlay?: ReactEventHandler<HTMLVideoElement>
}

const Background = ({ config, videoLoop, videoMuted, onVideoEnded, onVideoCanPlay }: BackgroundProps): JSX.Element => {
  const webcamRotation = useSelector((state: RootState) => state.viewReducer.configurations.webcamRotation)
  const imageRef = useRef<HTMLImageElement>(null)
  const dimensions = useRefDimensions(imageRef)

  const [videoDimensions, setVideoDimensions] = useState<{
    width?: number
    height?: number
  }>(dimensions)
  const [webcamStyle, setWebcamStyle] = useState<{
    width?: string
    height?: string
    transform?: string
  }>({})

  useEffect(() => {
    if (config.style === 'webcam') {
      if (!webcamRotation || webcamRotation % 180 === 0) {
        setVideoDimensions(dimensions)
      } else {
        setVideoDimensions({
          height: dimensions.width,
          width: dimensions.height
        })
      }
    }
  }, [config.style, dimensions])

  useEffect(() => {
    switch (webcamRotation) {
      case 90:
        setWebcamStyle({
          width: '100vh',
          height: '100vw',
          transform: 'rotate(90deg)'
        })
        break
      case -90:
        setWebcamStyle({
          width: '100vh',
          height: '100vw',
          transform: 'rotate(-90deg)'
        })
        break
      case 180:
        setWebcamStyle({ transform: 'rotate(180deg)' })
        break
      default:
        setWebcamStyle({})
        break
    }
  }, [webcamRotation])

  return (
    <React.Fragment>
      {/* Image background */}
      {config.style === 'image' && config.image && (
        <img ref={imageRef} src={config.image} alt='background image' className='rf-col-span-full rf-row-span-full rf-w-full rf-h-full rf-object-cover rf-filter rf-blur-background' />
      )}

      {/* Video background */}
      {config.style === 'video' && config.video && <BackgroundVideo source={config.video} loop={!!videoLoop} muted={!!videoMuted} onVideoEnded={onVideoEnded} onVideoCanPlay={onVideoCanPlay} />}

      {/* Webcam background */}
      {config.style === 'webcam' && <BackgroundWebcam config={config.webcam} size={videoDimensions} style={webcamStyle} />}
    </React.Fragment>
  )
}

export default Background
